import * as React from "react";
import { LucideIcon } from "lucide-react";

// --- Layout Components ---

export const SidebarLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex h-screen w-full bg-[#F5F5F5] overflow-hidden">
      {children}
    </div>
  );
};

export const SidebarRail = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return (
    <div className={`flex w-[64px] flex-col items-center flex-shrink-0 z-20 py-3 ${className}`}>
      {children}
    </div>
  );
};

export const Sidebar = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  return (
    <div className={`flex w-[240px] flex-col flex-shrink-0 z-10 overflow-y-auto ${className}`}>
      {children}
    </div>
  );
};

interface MainContentProps {
  children: React.ReactNode;
  fullWidth?: boolean;
  noPadding?: boolean;
}

export const MainContent = ({ children, fullWidth = false, noPadding = false }: MainContentProps) => {
  return (
    <div className="flex-1 overflow-y-auto min-w-0">
      <div className={`w-full ${fullWidth ? '' : 'max-w-7xl mx-auto'} ${noPadding ? '' : 'p-8'}`}>
        {children}
      </div>
    </div>
  );
};

// --- Rail Components ---

export const RailItem = ({ 
  icon: Icon, 
  active, 
  onClick, 
  className = "",
  label
}: { 
  icon: React.ReactNode | LucideIcon; 
  active?: boolean; 
  onClick?: () => void;
  className?: string;
  label?: string;
}) => {
  const IconComp = (Icon as any);
  
  const renderedIcon = React.isValidElement(Icon) 
    ? Icon 
    : <IconComp className="h-5 w-5" />;

  return (
    <div className="group relative flex items-center justify-center px-2 py-1">
       <button
        onClick={onClick}
        className={`relative flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-black/50 ${
          active 
            ? "bg-white text-black shadow-sm" 
            : "text-neutral-500 hover:bg-black/5 hover:text-black"
        } ${className}`}
      >
        {renderedIcon}
      </button>
      {label && (
          <div className="absolute left-full ml-3 hidden whitespace-nowrap rounded-md bg-black px-2 py-1 text-xs font-medium text-white group-hover:block z-50">
              {label}
          </div>
      )}
    </div>
  );
};

export const RailSpacer = () => <div className="flex-1" />;

export const RailDivider = () => <div className="mx-auto w-8 h-[1px] bg-neutral-200 my-2" />;

// --- Sidebar Components ---

export const SidebarHeader = ({ children }: { children: React.ReactNode }) => (
    <div className="px-4 py-4">
        {children}
    </div>
);

export const SidebarSection = ({ title, children }: { title?: string, children: React.ReactNode }) => (
    <div className="mb-2 px-2">
        {title && <h3 className="mb-2 px-2 text-xs font-medium text-neutral-500">{title}</h3>}
        <div className="space-y-0.5">
            {children}
        </div>
    </div>
);

interface SidebarItemProps {
  icon?: LucideIcon | React.ComponentType<{ className?: string }>;
  label: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  className?: string;
  badge?: React.ReactNode;
  right?: React.ReactNode;
}

export const SidebarItem = ({ icon: Icon, label, active, onClick, className = "", badge, right }: SidebarItemProps) => {
    return (
        <button
            onClick={onClick}
            className={`w-full group flex items-center h-8 gap-2.5 px-2 text-sm rounded-md transition-all duration-150 outline-none focus-visible:ring-2 focus-visible:ring-black/50 ${
                active
                    ? "bg-neutral-100 text-black font-medium"
                    : "text-neutral-600 hover:bg-neutral-50 hover:text-black font-medium"
            } ${className}`}
        >
            {Icon && <Icon className={`w-4 h-4 ${active ? "text-black" : "text-neutral-500 group-hover:text-black"}`} />}
            <span className="truncate flex-1 text-left leading-none">{label}</span>
            {badge && (
               <span className={`flex items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                   active ? "bg-white shadow-sm border border-neutral-200 text-black" : "bg-neutral-100 text-neutral-500"
               }`}>
                 {badge}
               </span>
            )}
            {right}
        </button>
    );
};
